import { ConfirmModal, MoreButton } from '@/components';
import { useMe } from '@/features/authentication/_hooks/me';
import { ContinueProjectModal } from '@/features/project/_components/ContinueProjectModal';
import {
  useDeleteProject,
  useLockProject,
  useProject,
  useUnlockProject,
} from '@/features/project/_hooks/project';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { isPS } from '@/utils';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

export const ProjectActionsButton: FC = () => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { id } = useId();
  const navigate = useNavigate();
  const location = useLocation();
  const project = useProject(id);
  const [activeModalType, setActiveModalType] = useState<
    'continue' | 'lock' | 'unlock' | 'delete' | null
  >(null);
  const { mutateAsync: lockProjectAsync } = useLockProject();
  const { mutateAsync: unlockProjectAsync } = useUnlockProject();
  const { mutateAsync: deleteProjectAsync } = useDeleteProject();
  const me = useMe();

  const hasData = project.data && me.data;
  const isLoading = project.isLoading || me.isLoading;

  if (isLoading || !hasData) {
    return null;
  }

  return (
    <>
      <MoreButton
        items={[
          // {
          //   key: 'continue',
          //   label: t('continue-project'),
          //   hidden: project.data.isDiscarded || !me.data.role.continueAbility,
          // },
          // {
          //   key: 'split',
          //   label: t('split-project'),
          //   hidden:
          //     project.data.sheetType === 'KS' ||
          //     project.data.isDiscarded ||
          //     !project.data.isConsecutive ||
          //     project.data.isLocked,
          // },
          {
            key: 'lock',
            label: t('lock-project'),
            hidden: !me.data.role.lockAbility || project.data.isLocked,
          },
          {
            key: 'unlock',
            label: t('unlock-project'),
            hidden: !me.data.role.lockAbility || !project.data.isLocked,
          },
          {
            key: 'delete',
            label: t('delete'),
            hidden: project.data.isLocked,
            danger: true,
          },
        ].filter((item) => !item.hidden)}
        onClick={({ key }) => {
          switch (key) {
            case 'split':
              navigate(
                isPS()
                  ? `${location.pathname.replace('/public-site', '')}/split`
                  : `${location.pathname}/split`,
              );
              break;
            case 'continue':
            case 'lock':
            case 'unlock':
            case 'delete':
              setActiveModalType(key);
              break;
          }
        }}
      />
      <ContinueProjectModal
        open={activeModalType === 'continue'}
        onClose={() => {
          setActiveModalType(null);
        }}
      />
      <ConfirmModal
        open={activeModalType === 'lock'}
        title={t('lock-confirmation')}
        disclaimerText={t('lock-item-confirmation', {
          item: project.data.name,
        })}
        onOk={async () => {
          try {
            await lockProjectAsync({ id: project.data.id });
            await Promise.all([
              openMessage('success', t('message-lock-completed')),
              (async () => {
                setActiveModalType(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-lock-failed'));
          }
        }}
        onCancel={() => {
          setActiveModalType(null);
        }}
        skipCheck
      />
      <ConfirmModal
        open={activeModalType === 'unlock'}
        title={t('unlock-confirmation')}
        disclaimerText={t('unlock-item-confirmation', {
          item: project.data.name,
        })}
        onOk={async () => {
          try {
            await unlockProjectAsync({ id: project.data.id });
            await Promise.all([
              openMessage('success', t('message-unlock-completed')),
              (async () => {
                setActiveModalType(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-unlock-failed'));
          }
        }}
        onCancel={() => {
          setActiveModalType(null);
        }}
        skipCheck
      />
      <ConfirmModal
        open={activeModalType === 'delete'}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-item-confirmation', {
          item: project.data.name,
        })}
        onOk={async () => {
          try {
            await deleteProjectAsync({ id: project.data.id });
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              (async () => {
                navigate('/rs/project');
                setActiveModalType(null);
              })(),
            ]);
          } catch {
            if (project.data.isPublished) {
              await openMessage(
                'error',
                t('message-published-project-delete-failed'),
              );
            } else {
              await openMessage('error', t('message-delete-failed'));
            }
          }
        }}
        onCancel={() => {
          setActiveModalType(null);
        }}
        skipCheck
      />
    </>
  );
};
